(() => {
  'use strict';

  const elementName = 'bennett-art-scroll-grid';

  if (customElements.get(elementName)) return;

  class BennettArtScrollGrid extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.reducedMotion ||= window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopMotion ||= window.matchMedia('(min-width: 1200px)');
      this.forceReducedMotion = new URLSearchParams(window.location.search).get('motion') === 'reduce';

      if (!this.mediaListenersBound) {
        this.handleMediaChange = () => this.reconfigure();
        this.reducedMotion.addEventListener('change', this.handleMediaChange);
        this.desktopMotion.addEventListener('change', this.handleMediaChange);
        this.mediaListenersBound = true;
      }

      const enabled = this.dataset.motionEnabled === 'true';
      const desktop = this.desktopMotion.matches;
      const hasGsap = Boolean(window.gsap && window.ScrollTrigger);

      if (!enabled || !desktop || this.reducedMotion.matches || this.forceReducedMotion || !hasGsap) {
        this.showStatic();
        return;
      }

      this.initializeMotion();
    }

    disconnectedCallback() {
      this.teardownMotion();
      if (this.mediaListenersBound) {
        this.reducedMotion?.removeEventListener('change', this.handleMediaChange);
        this.desktopMotion?.removeEventListener('change', this.handleMediaChange);
        this.mediaListenersBound = false;
      }
      this.initialized = false;
    }

    teardownMotion() {
      this.timeline?.scrollTrigger?.kill();
      this.timeline?.kill();
      this.resizeObserver?.disconnect();
      this.timeline = null;
      this.resizeObserver = null;

      const animatedLayers = this.querySelectorAll(
        '[data-art-grid], [data-art-row], [data-art-title], [data-art-lockup], [data-art-row] img'
      );
      window.gsap?.set(animatedLayers, { clearProps: 'transform,opacity,visibility' });
    }

    reconfigure() {
      this.teardownMotion();
      this.classList.remove('is-motion-ready', 'is-static', 'is-ready');
      this.style.removeProperty('--art-progress');
      this.initialized = false;

      requestAnimationFrame(() => {
        if (this.isConnected) this.connectedCallback();
      });
    }

    showStatic() {
      this.classList.remove('is-motion-ready');
      this.classList.add('is-static', 'is-ready');
      this.setLockupInteractive(true);
    }

    initializeMotion() {
      const { gsap, ScrollTrigger } = window;
      const grid = this.querySelector('[data-art-grid]');
      const topRow = this.querySelector('[data-art-row="top"]');
      const bottomRow = this.querySelector('[data-art-row="bottom"]');
      const title = this.querySelector('[data-art-title]');
      const lockup = this.querySelector('[data-art-lockup]');
      const topImages = this.querySelectorAll('[data-art-row="top"] img');
      const bottomImages = this.querySelectorAll('[data-art-row="bottom"] img');

      if (!grid || !topRow || !bottomRow || !title || !lockup) {
        this.showStatic();
        return;
      }

      gsap.registerPlugin(ScrollTrigger);
      this.classList.remove('is-static');
      this.classList.add('is-motion-ready', 'is-ready');
      this.setLockupInteractive(false);

      gsap.set(grid, { xPercent: -50, yPercent: -50, scale: 1.31 });
      gsap.set(topRow, { x: '-7vw' });
      gsap.set(bottomRow, { x: '7vw' });
      gsap.set(topImages, { yPercent: -7 });
      gsap.set(bottomImages, { yPercent: -3 });
      gsap.set(title, { xPercent: -50, yPercent: 0, autoAlpha: 1, scale: .96 });
      gsap.set(lockup, { xPercent: -50, autoAlpha: 0, y: -10 });

      this.timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: this,
          start: 'top top',
          end: 'bottom bottom',
          scrub: .42,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            this.style.setProperty('--art-progress', self.progress.toFixed(4));
            this.setLockupInteractive(self.progress >= .82);
          }
        }
      });

      this.timeline
        .addLabel('introduction', 0)
        .to(title, { autoAlpha: 1, scale: 1, duration: .7, ease: 'power2.out' }, 0)
        .to(grid, { scale: 1.22, duration: 1.25, ease: 'power2.inOut' }, 0)
        .to(topRow, { x: '-3.6vw', duration: 1.25, ease: 'power2.inOut' }, 0)
        .to(bottomRow, { x: '3.6vw', duration: 1.25, ease: 'power2.inOut' }, 0)

        .addLabel('unfold', 1.05)
        .to(title, { autoAlpha: 0, y: '-3vh', scale: 1.025, duration: .85, ease: 'power2.in' }, 1.05)
        .to(grid, { scale: 1, duration: 2.5, ease: 'power3.inOut' }, 1.05)
        .to(topRow, { x: 0, duration: 2.5, ease: 'power3.inOut' }, 1.05)
        .to(bottomRow, { x: 0, duration: 2.5, ease: 'power3.inOut' }, 1.05)
        .to(topImages, { yPercent: -3, duration: 2.5, stagger: .04, ease: 'power2.inOut' }, 1.05)
        .to(bottomImages, { yPercent: -7, duration: 2.5, stagger: .04, ease: 'power2.inOut' }, 1.05)

        .addLabel('collection', 3.35)
        .to(grid, { scale: .985, duration: .7, ease: 'power2.out' }, 3.35)
        .to(lockup, { autoAlpha: 1, y: 0, duration: .55, ease: 'power2.out' }, 3.55)
        .addLabel('hold', 4.1)
        .to({}, { duration: .65 })
        .addLabel('timelineEnd', 4.75);

      this.refreshAfterImages(ScrollTrigger);
      this.resizeObserver = new ResizeObserver(() => ScrollTrigger.refresh());
      this.resizeObserver.observe(this);
    }

    refreshAfterImages(ScrollTrigger) {
      requestAnimationFrame(() => ScrollTrigger.refresh());
      const pendingImages = [...this.querySelectorAll('img')].filter((image) => !image.complete);
      if (!pendingImages.length) return;

      Promise.all(
        pendingImages.map(
          (image) => new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          })
        )
      ).then(() => ScrollTrigger.refresh());
    }

    setLockupInteractive(interactive) {
      const lockup = this.querySelector('[data-art-lockup]');
      const link = lockup?.querySelector('a');
      if (!lockup || !link) return;
      lockup.setAttribute('aria-hidden', interactive ? 'false' : 'true');
      link.tabIndex = interactive ? 0 : -1;
    }
  }

  customElements.define(elementName, BennettArtScrollGrid);
})();
